import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import type { SelectHTMLAttributes } from "react";
import { useId } from "react";

export interface GfgSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface GfgSelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  options: GfgSelectOption[];
  /** Red border for a failed-validation state. */
  error?: boolean;
  /** Message describing the error, announced to screen readers and shown below the
   *  field (wired via aria-describedby). Renders only when `error` is also true. */
  errorText?: string;
  surface?: "white" | "background";
  /** Tell password managers to skip this field (non-credential forms). */
  ignorePasswordManagers?: boolean;
}

/** GfG canonical select — floating notch label + custom chevron, matched to GfgTextField. */
export function GfgSelect({
  label,
  options,
  error = false,
  errorText,
  surface = "white",
  className = "",
  ignorePasswordManagers = false,
  id,
  ...rest
}: GfgSelectProps) {
  const autoId = useId();
  const selId = id ?? autoId;
  const errId = `${selId}-err`;
  const showError = error && !!errorText;
  const surfaceCls = surface === "background" ? "bg-background" : "bg-white";
  const borderCls = error ? "border-good-wine" : "border-smoke";
  const pwIgnore = ignorePasswordManagers ? "true" : undefined;

  return (
    <div className={`relative w-full ${className}`.trim()}>
      <label
        htmlFor={selId}
        className={`absolute -top-[7px] left-[10px] z-[1] ${surfaceCls} px-[5px] text-[10px] font-bold uppercase tracking-[1.5px] text-dust-ink`}
      >
        {label}
      </label>
      <select
        id={selId}
        aria-invalid={error || undefined}
        aria-describedby={showError ? errId : undefined}
        className={`w-full cursor-pointer appearance-none rounded-[8px] border ${borderCls} bg-white py-[12px] pl-[14px] pr-[38px] font-lato text-[14px] text-rodeo-black focus:border-dust-ink focus:outline-none`}
        {...rest}
        data-1p-ignore={pwIgnore}
        data-lpignore={pwIgnore}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
      </select>
      <ExpandMoreIcon
        fontSize="small"
        className="pointer-events-none absolute right-[10px] top-1/2 -translate-y-1/2 text-graphite"
      />
      {showError ? (
        <p id={errId} className="mt-[6px] px-[2px] font-lato text-[12px] text-good-wine">
          {errorText}
        </p>
      ) : null}
    </div>
  );
}

export default GfgSelect;
